import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

// project imports
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import ProtectedRoute from './ProtectedRoute';
import EmployeeListing from 'pages/warehouse/EmployeeListing';
import EmployeeDetails from 'pages/warehouse/EmployeeDetails';
import NotFound from 'pages/NotFound';
import ChunkLoadError from 'pages/ChunkLoadError';

// Retry dynamic import on failure (e.g. stale chunk after dev restart or next-day open)
const lazyWithRetry = (importFn, retries = 2, delay = 1000) =>
  lazy(
    () =>
      new Promise((resolve, reject) => {
        const tryLoad = (attempt) =>
          importFn()
            .then(resolve)
            .catch((err) => {
              if (attempt >= retries) reject(err);
              else setTimeout(() => tryLoad(attempt + 1), delay);
            });
        tryLoad(0);
      })
  );

// render- Dashboard
const DashboardDefault = Loadable(lazyWithRetry(() => import('pages/dashboard/default')));
const ViewWarehouse = Loadable(lazyWithRetry(() => import('pages/warehouse/ViewWarehouse')));
const WarehouseDetails = Loadable(lazyWithRetry(() => import('pages/warehouse/WarehouseDetails')));
const ChamberListing = Loadable(lazyWithRetry(() => import('pages/warehouse/ChamberListing')));
const ChamberDetails = Loadable(lazyWithRetry(() => import('pages/warehouse/ChamberDetails')));
const ChamberLayout = Loadable(lazyWithRetry(() => import('pages/warehouse/ChamberLayout')));
const WarehouseLayoutBuilder = Loadable(lazyWithRetry(() => import('pages/warehouse/WarehouseLayoutBuilder')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  element: (
    <ProtectedRoute>
      <DashboardLayout />
    </ProtectedRoute>
  ),
  errorElement: <ChunkLoadError />,
  children: [
    {
      index: true,
      element: <DashboardDefault />
    },
    {
      path: 'dashboard',
      children: [
        {
          index: true,
          element: <DashboardDefault />
        },
        {
          path: 'default',
          element: <DashboardDefault />
        }
      ]
    },
    {
      path: 'wareHouse',
      children: [
        {
          path: 'view',
          element: <ViewWarehouse />
        },
        {
          path: 'details',
          element: <Navigate to="/wareHouse/view" replace />
        },
        {
          path: 'details/:id',
          element: <WarehouseDetails />
        },
        {
          path: 'details/:id/chamber',
          element: <ChamberListing />
        },
        {
          path: 'details/:id/chamber/details/:chamberId',
          element: <ChamberDetails />
        },
        {
          path: 'details/:id/chamber/details/:chamberId/layout',
          element: <ChamberLayout />
        },
        {
          path: 'details/:id/layout',
          element: <WarehouseLayoutBuilder />
        },
        {
          path: 'details/:id/employees',
          element: <EmployeeListing />
        },
        {
          path: 'details/:id/employees/details/:employeeId',
          element: <EmployeeDetails />
        }
      ]
    },
    {
      path: 'not-found',
      element: <NotFound />
    },
    {
      path: '*',
      element: <Navigate to="/not-found" replace />
    }
  ]
};

export default MainRoutes;
